/**
 * Настройки: `GET /admin/settings` отдаёт все пары «ключ → значение»
 * строками, `PATCH` принимает только изменённые и возвращает полный набор.
 *
 * Значения приходят строками намеренно: ключей десятки, и типизировать их
 * по одному значило бы держать копию серверного справочника. Экран разбирает
 * ровно те, которые показывает.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { parseMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { PAGE_LANGS, toRequests, type PageEdit, type PageLang, type PageSlug } from './pages'

type AdminPageDto = components['schemas']['AdminPage']

export type Settings = Record<string, string>

export function fetchSettings(api: ApiClient): Promise<Settings> {
  return api.get<Settings>('/admin/settings')
}

/** Число из настройки; неизвестный ключ или мусор — запасное значение. */
export function num(settings: Settings, key: string, fallback: number): number {
  const value = Number(settings[key])
  return Number.isFinite(value) ? value : fallback
}

export function useSettings() {
  const api = useApi()
  return useQuery({ queryKey: keys.settings, queryFn: () => fetchSettings(api) })
}

export function useSaveSettings() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (patch: Settings) => api.patch<Settings>('/admin/settings', patch),
    onSuccess: (settings) => {
      client.setQueryData(keys.settings, settings)
    },
  })
}

/** Язык страницы, как его показывает редактор. Ноль в `updatedAt` — не писали. */
export interface PageLangText {
  title: string
  body: string
  updatedAt: number
}

export type Page = Record<PageLang, PageLangText>

/**
 * Сервер всегда присылает все три языка, но схема их не обещает: язык, которого
 * нет в ответе, читаем пустым, а не роняем экран.
 */
export function toPage(dto: AdminPageDto): Page {
  const texts = dto.texts ?? {}
  return Object.fromEntries(
    PAGE_LANGS.map((lang) => {
      const text = texts[lang] ?? {}
      return [
        lang,
        { title: text.title ?? '', body: text.body ?? '', updatedAt: parseMs(text.updated_at) },
      ]
    }),
  ) as Page
}

/** Страница для редактора: `GET /admin/pages/{slug}`, все языки сразу. */
export function usePage(slug: PageSlug) {
  const api = useApi()
  return useQuery({
    queryKey: keys.page(slug),
    queryFn: async () => toPage(await api.get<AdminPageDto>(`/admin/pages/${slug}`)),
  })
}

/**
 * Сохранение правок: по запросу `PUT /admin/pages/{slug}` на страницу, в теле —
 * только изменённые языки. Ответ — страница целиком, она и становится
 * сохранённой.
 */
export function useSavePages() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (edits: PageEdit[]) => {
      for (const { slug, texts } of toRequests(edits)) {
        const dto = await api.put<AdminPageDto>(`/admin/pages/${slug}`, { texts })
        client.setQueryData(keys.page(slug), toPage(dto))
      }
    },
  })
}
